import { computed } from 'vue'
import { useProjectApi } from '/@/common/projects/api'
import { useSectionApi } from '/@/common/projects/api/section'
import { useProjectThingApi } from '/@/common/projects/api/thing'
import {
  type ProjectCategory,
  type ProjectClientType,
  type ProjectReason,
  type ProjectVisibility,
  type ProjectProductDisplayType,
} from '../../types'
import {
  type ProjectImageSection,
  type ProjectProductSection,
  type ProjectWizardPanel,
  type ProjectWizardProject,
} from './types'
import { useBudgetApi } from '/@/common/budget/api'
import { useImages } from '/@/common/images/api'
import { type Asset } from '/@/common/images/types'
import { useProductApi } from '/@/common/products/api'
import { type ProductInput } from '/@/common/products/types'
import { useRecommendationApi } from '/@/common/recommendation'
import {
  type ProjectImage,
  type ProjectMockup,
  type ProjectProduct,
  type ProjectThing,
  type ProjectThingInput,
  type Thing,
  type ThingInput,
} from '/@/common/things/types'
import { createModal } from '/@/core/elements/modal'
import { type StellaMenuListSingleItem } from '/@/stella/StellaMenu'

interface ProjectWizardModalData {
  initialPanel?: ProjectWizardPanel
  initialProjectId: string | null
}

const { api, component } = createModal<ProjectWizardModalData>(
  'ProjectWizardModal',
  () => import('./ProjectWizardModal.vue'),
)

export const projectReasons: StellaMenuListSingleItem[] = [
  {
    type: 'item',
    id: 'play',
    label: `I'm just playing around`,
  },
  {
    type: 'item',
    id: 'new_space',
    label: `I'm designing for a brand new space`,
  },
  {
    type: 'item',
    id: 'current_space',
    label: 'I want to update an existing space',
  },
  {
    type: 'item',
    id: 'renovation',
    label: `I'm renovating`,
  },
  {
    type: 'item',
    id: 'other',
    label: 'Other',
  },
]

export const projectCategories: StellaMenuListSingleItem[] = [
  {
    type: 'item',
    id: 'house',
    label: 'House',
  },
  {
    type: 'item',
    id: 'apartment',
    label: 'Apartment',
  },
  {
    type: 'item',
    id: 'room',
    label: 'Room',
  },
  {
    type: 'item',
    id: 'business',
    label: 'Business',
  },
  {
    type: 'item',
    id: 'other',
    label: 'Other',
  },
]

export function useProjectWizardUtils() {
  const projectApi = useProjectApi()
  const budgetApi = useBudgetApi()
  const sectionApi = useSectionApi(computed(() => null))
  const images = useImages()
  const projectThingApi = useProjectThingApi()
  const recommendationApi = useRecommendationApi()
  const productApi = useProductApi()

  async function createProject(
    name: string,
    visibility: ProjectVisibility,
    category?: ProjectCategory,
    reason?: ProjectReason,
    clientType?: ProjectClientType,
    productDisplayType?: ProjectProductDisplayType,
  ): Promise<string | undefined> {
    const result = await projectApi.create({
      name,
      visibility,
      category,
      reason,
      clientType,
      productDisplayType,
    })
    return result?.id
  }

  async function addBudget(projectId: string, amount: number) {
    const status = 'DRAFT'
    return await budgetApi.createBudget(projectId, {
      amountCents: amount,
      status,
    })
  }

  async function getSectionId(
    projectId: string,
    sectionName: ProjectImageSection | ProjectProductSection,
    maybeSectionId?: string,
  ): Promise<string | null | undefined> {
    if (maybeSectionId) return maybeSectionId

    const sectionId = await sectionApi
      .create({
        projectId: projectId,
        name: sectionName,
      })
      .then(res => res?.data?.project.section.create.recordId)

    return sectionId
  }

  async function addImagesToSection(
    project: ProjectWizardProject | null,
    imageSelection: Asset[],
    sectionName: ProjectImageSection,
  ) {
    if (!project || !imageSelection.length) return

    const maybeSectionId = project.sections.find(
      p => p.name === sectionName,
    )?.id

    const sectionId = await getSectionId(
      project?.id,
      sectionName,
      maybeSectionId,
    )

    const rawImages = imageSelection.map(asset => ({
      url: asset.url,
      name: asset.name || 'Untitled',
      sourceUrl: asset.sourceUrl,
    }))

    const imageThings = await images.add({ images: rawImages })

    if (!imageThings) return

    await projectThingApi.addToProject({
      projectId: project.id,
      sectionId,
      things: imageThings.map(i => ({ thingId: i.id, thingType: 'IMAGE' })),
    })

    if (sectionName === 'Inspo' && project.clientType === 'CLIENT') {
      await projectApi.updateCover({
        projectId: project.id,
        imageUrl: rawImages[0].url,
      })
    }

    return { sectionId, images: imageThings }
  }

  async function fetchRecommendations(
    things: ProjectThing[],
    sectionName: ProjectImageSection,
  ) {
    let recommendations = []
    const projectThings: ProjectImage[] = things.filter(
      thing =>
        thing.section?.name === sectionName && thing.thingType === 'IMAGE',
    ) as ProjectImage[]

    if (projectThings.length) {
      const imageUrls: string[] = projectThings.map(thing => thing.imageUrl)
      const recs = await recommendationApi.fetchProductRecommendations({
        imageUrls,
      })

      recommendations = recs?.data?.recommendation.product.products
    }

    return recommendations
  }

  async function addProductsToProject(
    projectId: string,
    things: Array<ThingInput | ProjectThingInput>,
    sectionId?: ProjectProductSection,
  ) {
    return await projectThingApi.addToProject({
      projectId,
      sectionId,
      things: things,
    })
  }

  async function addProductsToSection(
    project: ProjectWizardProject | null,
    sectionName: ProjectProductSection,
    things: Array<ThingInput | ProjectThingInput>,
  ) {
    if (!project || !things.length) return

    const ownedSectionId = project.sections.find(
      p => p.name === sectionName,
    )?.id

    const sectionId = (await getSectionId(
      project.id,
      'Owned Products',
      ownedSectionId,
    )) as ProjectProductSection

    await addProductsToProject(project.id, things, sectionId)

    return { sectionId }
  }

  async function importAndAddProduct(projectId: string, input: ProductInput) {
    const productResult = await productApi.create(input)
    if (productResult?.status === 200 && productResult.record) {
      await addProduct(projectId, productResult.record.id)
      return productResult.record
    } else {
      const errorMessage = productResult?.message?.text
        ? 'Oops! We were unable to import the product. ' +
          productResult?.message?.text
        : 'Oops! We were unable to import the product.'
      throw new Error(errorMessage)
    }
  }

  async function addProduct(projectId: string, productId: string) {
    await addProductsToProject(projectId, [
      { thingId: productId, thingType: 'PRODUCT' },
    ])

    return productId
  }

  function getCurrentPhotos(
    project: ProjectWizardProject | null,
    sectionName: ProjectImageSection,
  ) {
    if (project) {
      return project.things
        .filter(thing => {
          return thing.section?.name === sectionName
        })
        .map(thing => {
          switch (thing.thingType) {
            case 'IMAGE':
              return (thing as ProjectImage).imageUrl
            case 'PRODUCT':
              return (thing as ProjectProduct).imageUrl
            case 'MOCKUP':
              return (thing as ProjectMockup).mockup.imageUrl
          }
        })
        .filter(url => url !== undefined) as string[]
    }
    return []
  }

  const thingToThingInput = (
    thing: Thing | ProjectThing,
  ): ThingInput | ProjectThingInput => ({
    thingId: thing.id ? thing.id : thing.thingType,
    thingType: thing.thingType,
  })

  return {
    createProject,
    addBudget,
    addImagesToSection,
    fetchRecommendations,
    addProductsToProject,
    addProductsToSection,
    addProduct,
    importAndAddProduct,
    getCurrentPhotos,
    thingToThingInput,
  }
}

export const useProjectWizardModal = api
export default component
